//src/pages/cadastros/UsersPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CrudRowActions } from "@/components/shared/CrudRowActions";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  useUsers,
  useSetUserActive,
  useSetUserPassword,
} from "@/hooks/useUsers";
import type { UserListItem } from "@/services/users";
import { maskCNPJ, maskCPF, onlyDigits } from "@/lib/masks";
import { useAuth } from "@/contexts/AuthContext";
import { PERMISSIONS } from "@/lib/permissions";
import { PermissionButton } from "@/components/shared/PermissionButton";

type StatusFilter = "active" | "inactive" | "all";

const LIMIT = 50;

function normalizeText(v: string) {
  return (v || "").toLowerCase().trim();
}

function passwordRules(password: string) {
  const s = password || "";
  const hasMin = s.length >= 8;
  const hasUpper = /[A-Z]/.test(s);
  const hasLower = /[a-z]/.test(s);
  const hasNumber = /[0-9]/.test(s);

  return {
    hasMin,
    hasUpper,
    hasLower,
    hasNumber,
    ok: hasMin && hasUpper && hasLower && hasNumber,
  };
}

function formatDocumentByType(
  documentNumber?: string | null,
  personType?: "PF" | "PJ" | string | null,
) {
  const digits = onlyDigits(documentNumber || "");
  if (!digits) return "-";

  if (personType === "PJ") return maskCNPJ(digits);
  if (personType === "PF") return maskCPF(digits);

  if (digits.length <= 11) return maskCPF(digits);
  return maskCNPJ(digits);
}

function generateStrongPassword(length = 12) {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const numbers = "23456789";
  const symbols = "@#$%&*!?";
  const all = upper + lower + numbers + symbols;

  const pick = (chars: string) =>
    chars[Math.floor(Math.random() * chars.length)];

  const base = [
    pick(upper),
    pick(lower),
    pick(numbers),
    pick(symbols),
  ];

  while (base.length < length) {
    base.push(pick(all));
  }

  for (let i = base.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [base[i], base[j]] = [base[j], base[i]];
  }

  return base.join("");
}

export default function UsersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [offset, setOffset] = useState(0);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selected, setSelected] = useState<UserListItem | null>(null);

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<UserListItem | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const userPermissions = user?.permissions ?? [];
  const canReadUsers = userPermissions.includes(PERMISSIONS.USERS_READ);
  const canWriteUsers = userPermissions.includes(PERMISSIONS.USERS_WRITE);
  const canChangeUserPassword = userPermissions.includes(
    PERMISSIONS.USERS_PASSWORD_CHANGE,
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [debouncedSearch, statusFilter]);

  const params = useMemo(
    () => ({
      q: debouncedSearch ?? "",
      limit: LIMIT,
      offset,
    }),
    [debouncedSearch, offset],
  );

  const usersQuery = useUsers(params);
  const setActiveMut = useSetUserActive();
  const setPasswordMut = useSetUserPassword();

  const allUsers = usersQuery.data ?? [];

  const filtered = useMemo(() => {
    const q = normalizeText(debouncedSearch);

    let base = allUsers;
    if (statusFilter === "active") base = base.filter((u) => u.is_active);
    else if (statusFilter === "inactive") base = base.filter((u) => !u.is_active);

    if (!q) return base;

    return base.filter((u) => {
      const name = normalizeText(u.name);
      const email = normalizeText(u.email ?? "");
      const doc = normalizeText(u.document_number ?? "");
      const unit = normalizeText(u.unit_name ?? "");
      const employeeCode = normalizeText(u.employee_code ?? "");
      const jobPosition = normalizeText(u.job_position_name ?? "");
      const department = normalizeText(u.department_name ?? "");
      const personType = normalizeText(u.person_type ?? "");

      return (
        name.includes(q) ||
        email.includes(q) ||
        doc.includes(q) ||
        unit.includes(q) ||
        employeeCode.includes(q) ||
        jobPosition.includes(q) ||
        department.includes(q) ||
        personType.includes(q)
      );
    });
  }, [allUsers, debouncedSearch, statusFilter]);

  const hasNextPage = allUsers.length === LIMIT;
  const canPrev = offset > 0;
  const isBusy = usersQuery.isFetching || setActiveMut.isPending || setPasswordMut.isPending;

  const pw = useMemo(() => passwordRules(newPassword), [newPassword]);
  const confirmOk = newPassword.length > 0 && confirmPassword === newPassword;

  function openToggle(user: UserListItem) {
    if (!canWriteUsers) return;
    setSelected(user);
    setConfirmOpen(true);
  }

  function openPasswordDialog(user: UserListItem) {
    if (!canChangeUserPassword) return;
    setPasswordTarget(user);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setPasswordDialogOpen(true);
  }

  function handleSuggestPassword() {
    const suggested = generateStrongPassword(12);
    setNewPassword(suggested);
    setConfirmPassword(suggested);
    setShowNewPassword(true);
    setShowConfirmPassword(true);
    toast.success("Senha forte sugerida e preenchida.");
  }

  async function handleConfirmToggle() {
    if (!selected || !canWriteUsers) return;

    const nextActive = !selected.is_active;

    try {
      await setActiveMut.mutateAsync({
        id: selected.id,
        is_active: nextActive,
      });

      toast.success(nextActive ? "Usuário ativado" : "Usuário desativado");
      setConfirmOpen(false);
      setSelected(null);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar status");
    }
  }

  async function handleChangePassword() {
    if (!passwordTarget || !canChangeUserPassword) return;

    if (!newPassword) {
      toast.error("Informe a nova senha");
      return;
    }

    if (!pw.ok) {
      toast.error("A senha não atende aos critérios mínimos");
      return;
    }

    if (!confirmOk) {
      toast.error("Senha e confirmação devem ser iguais");
      return;
    }

    try {
      await setPasswordMut.mutateAsync({
        id: passwordTarget.id,
        password: newPassword,
      });

      toast.success("Senha alterada com sucesso");
      setPasswordDialogOpen(false);
      setPasswordTarget(null);
      setNewPassword("");
      setConfirmPassword("");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao alterar senha");
    }
  }

  function closePasswordDialog() {
    setPasswordDialogOpen(false);
    setPasswordTarget(null);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  }

  return (
    <div>
      <PageHeader title="Usuários" description="Cadastro de usuários com acesso ao sistema">
        <PermissionButton
          permission={PERMISSIONS.USERS_WRITE}
          onClick={() => navigate("/cadastros/usuarios/novo")}
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Usuário
        </PermissionButton>
      </PageHeader>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, tipo, documento, unidade, matrícula, cargo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Ativos</SelectItem>
            <SelectItem value="inactive">Inativos</SelectItem>
            <SelectItem value="all">Todos</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            disabled={!canPrev || usersQuery.isFetching}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Anterior
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setOffset((o) => o + LIMIT)}
            disabled={!hasNextPage || usersQuery.isFetching}
          >
            Próxima
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </div>

      {usersQuery.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando usuários...
        </Card>
      ) : usersQuery.isError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            {(usersQuery.error as any)?.message || "Erro ao carregar usuários"}
          </div>
          <div className="mt-3">
            <Button variant="outline" onClick={() => usersQuery.refetch()}>
              Tentar novamente
            </Button>
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState title="Nenhum usuário encontrado" />
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Documento</TableHead>
                <TableHead>Unidade</TableHead>
                <TableHead>Matrícula</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Departamento</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[160px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filtered.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">
                    <div className="flex flex-col">
                      <span>{u.name}</span>
                      {u.email ? (
                        <span className="text-xs text-muted-foreground">{u.email}</span>
                      ) : null}
                    </div>
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {u.person_type ?? "-"}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {formatDocumentByType(u.document_number, u.person_type)}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {u.unit_name ?? "-"}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {u.employee_code ?? "-"}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {u.job_position_name ?? "-"}
                  </TableCell>

                  <TableCell className="text-sm text-muted-foreground">
                    {u.department_name ?? "-"}
                  </TableCell>

                  <TableCell>
                    <Badge
                      variant={u.is_active ? "default" : "secondary"}
                      className="text-xs"
                    >
                      {u.is_active ? "Ativo" : "Inativo"}
                    </Badge>
                  </TableCell>

                  <TableCell className="text-right">
                    <CrudRowActions
                      onView={() => navigate(`/cadastros/usuarios/${u.id}`)}
                      onEdit={() => navigate(`/cadastros/usuarios/${u.id}/editar`)}
                      onPassword={() => openPasswordDialog(u)}
                      onToggleActive={() => openToggle(u)}
                      isActive={u.is_active}
                      disabled={isBusy}
                      viewPermission={PERMISSIONS.USERS_READ}
                      editPermission={PERMISSIONS.USERS_WRITE}
                      passwordPermission={PERMISSIONS.USERS_PASSWORD_CHANGE}
                      toggleActivePermission={PERMISSIONS.USERS_WRITE}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={selected?.is_active ? "Desativar Usuário" : "Ativar Usuário"}
        description={
          selected?.is_active
            ? "Deseja desativar este usuário? Ele ficará impedido de acessar o sistema."
            : "Deseja ativar este usuário novamente?"
        }
        onConfirm={handleConfirmToggle}
        confirmLabel={selected?.is_active ? "Desativar" : "Ativar"}
        variant={selected?.is_active ? "destructive" : "default"}
      />

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar senha</DialogTitle>
            <DialogDescription>
              {passwordTarget
                ? `Defina uma nova senha para ${passwordTarget.name}.`
                : "Defina a nova senha do usuário."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div className="text-sm text-muted-foreground">
                Gere uma senha automática que já atende às regras mínimas.
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleSuggestPassword}
                className="gap-2"
              >
                <Wand2 className="h-4 w-4" />
                Sugerir senha forte
              </Button>
            </div>

            <div>
              <Label>Nova senha</Label>
              <div className="relative mt-1">
                <Input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="********"
                  className="pr-10"
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-8 w-8"
                  onClick={() => setShowNewPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div>
              <Label>Confirmar nova senha</Label>
              <div className="relative mt-1">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="********"
                  className="pr-10"
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-8 w-8"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="rounded-lg border p-3 text-sm text-muted-foreground">
              <div className="mb-2 font-medium text-foreground">Regras da senha</div>
              <ul className="ml-5 list-disc space-y-1">
                <li className={pw.hasMin ? "text-foreground" : ""}>Mínimo de 8 caracteres</li>
                <li className={pw.hasUpper ? "text-foreground" : ""}>Ao menos 1 letra maiúscula</li>
                <li className={pw.hasLower ? "text-foreground" : ""}>Ao menos 1 letra minúscula</li>
                <li className={pw.hasNumber ? "text-foreground" : ""}>Ao menos 1 número</li>
                <li className={confirmOk ? "text-foreground" : ""}>Confirmação igual à senha</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closePasswordDialog}>
              Cancelar
            </Button>

            <Button onClick={handleChangePassword} disabled={setPasswordMut.isPending}>
              {setPasswordMut.isPending ? "Salvando..." : "Salvar senha"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
